import { ChangeDetectionStrategy, Component, OnInit, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { UserGroupService } from 'src/app/modules/settings/services/user-group-service';
import { UserGroupViewModel } from 'src/app/modules/settings/viewModels/userGroups/userGroupViewModel';
import { ObjectTypes } from 'src/app/modules/shared/enums/objectTypes';
import { LocalisationService } from 'src/app/modules/shared/services/localisation.service';
import { EmployeeDetailsViewModel } from 'src/app/modules/shared/viewModels/employeeDetailsViewModel';
import { T } from 'src/assets/i18n/translation-keys';

@Component({
    selector: 'app-add-groups-to-employee-modal',
    templateUrl: './add-groups-to-employee-modal.component.html',
    styleUrls: ['./add-groups-to-employee-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AddGroupsToEmployeeComponent implements OnInit {
  description: string;
  showDelete: boolean = false;
  headerText: string = 'Add Employees To Group';
  maxLength: number = 200;
  employee: EmployeeDetailsViewModel;
  groups: UserGroupViewModel[] = [];
  selectedGroups: UserGroupViewModel[] = [];
  groupsBySymbol: { [key: string]: UserGroupViewModel[] } = {};
  groupsSelected: { [key: number]: boolean } = {};
  symbols: Set<string> = new Set();
  selectedSymbols: Set<string> = new Set();
  localisedGroups: string = 'Employee Groups';
  originalGroups: UserGroupViewModel[] = [];
  onSave = new EventEmitter<UserGroupViewModel[]>();
  loading = false;
  public readonly T = T;

  constructor(
    private readonly bsModalRef: BsModalRef,
    private readonly localisationService: LocalisationService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly groupsService: UserGroupService,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit() {
    this.localisedGroups = this.localisationService.localiseObjectType(ObjectTypes.User_Group, true);
    this.headerText = this.translateService.instant(
      T.settings.employees_departments_org.manage_employee.add_localisedGroups_to_employee_with_names,
      {
        localiseduserGroups: this.localisedGroups,
        localisedEmployee: this.localisationService.localiseObjectType(ObjectTypes.Employee),
        employeeNames: `${this.employee.firstName} ${this.employee.surname}`,
      }
    );
    this.loading = true;
    this.groupsService.list().subscribe((res) => {
      this.groups = res;
      this.originalGroups = JSON.parse(JSON.stringify(res));

      this.initRightPane();

      this.selectedGroups.forEach((g) => {
        const group = this.groups.find((e) => e.id == g.id);

        if (group) {
          this.onChecked(group, true, true);
        }
      });

      this.loading = false;
      this.changeDetectorRef.markForCheck();
    });
  }

  onSaveAndExit(): void {
    this.onSave.next(this.selectedGroups);
    this.bsModalRef.hide();
  }

  onClose(): void {
    this.bsModalRef.hide();
  }

  onChecked(group: UserGroupViewModel, selected: boolean, isInitial: boolean = false) {
    if (group.id) {
      this.groupsSelected[group.id] = selected;
    }

    if (!isInitial) {
      if (selected) {
        this.selectedGroups.push(group);
      } else {
        this.selectedGroups = this.selectedGroups.filter((g) => g.id != group.id);
      }
    }

    this.selectedGroups = this.selectedGroups.slice();
    this.changeDetectorRef.markForCheck();
  }

  private initRightPane(groups: UserGroupViewModel[] = this.groups) {
    this.symbols = new Set();
    this.groupsBySymbol = {};

    groups = groups.sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));

    groups.forEach((cap) => {
      const startingLetter = cap.title[0]?.toUpperCase();

      if (startingLetter != undefined) {
        if (!this.symbols.has(startingLetter)) {
          this.symbols.add(startingLetter);
        }

        if (this.groupsBySymbol[startingLetter] == undefined) {
          this.groupsBySymbol[startingLetter] = [cap];
        } else {
          this.groupsBySymbol[startingLetter].push(cap);
        }
      }
    });

    this.changeDetectorRef.markForCheck();
  }
  onSearch(input: string) {
    if (!input) {
      this.groups = this.originalGroups.slice();
      this.initRightPane();
      return;
    }

    this.groups = this.groups.filter((c) => c.title.toLowerCase().indexOf(input.toLowerCase()) !== -1);
    this.initRightPane();
  }
}
